import React from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '../../context/ThemeContext';

export interface TintIconProps {
  icon: React.ReactElement;
  /** Tint color — defaults to theme primary */
  color?: string;
  /** Size in px — defaults to 32 */
  size?: number | { xs?: number; sm?: number; md?: number };
  /** Border radius — defaults to '6px'. Use '50%' for circular */
  borderRadius?: string;
  /** Show border — defaults to true */
  bordered?: boolean;
  /** Background opacity hex suffix — defaults to '10' */
  tintOpacity?: string;
  /** Border opacity hex suffix — defaults to '20' */
  borderOpacity?: string;
  sx?: Record<string, unknown>;
}

/**
 * Decorative icon container with light tint background.
 * Used beside headings, labels, and list items as visual indicators.
 *
 * NOT interactive — for clickable icons use AppIconButton.
 */
const TintIcon: React.FC<TintIconProps> = ({
  icon,
  color,
  size = 32,
  borderRadius = '6px',
  bordered = true,
  tintOpacity = '10',
  borderOpacity = '20',
  sx,
}) => {
  const { theme } = useTheme();
  const tintColor = color || theme.primary;

  const sizeValue = typeof size === 'number'
    ? { width: size, height: size }
    : {
        width: { xs: size.xs || 28, sm: size.sm || 32, md: size.md || size.sm || 32 },
        height: { xs: size.xs || 28, sm: size.sm || 32, md: size.md || size.sm || 32 },
      };

  return (
    <Box
      sx={{
        ...sizeValue,
        borderRadius,
        background: `${tintColor}${tintOpacity}`,
        border: bordered ? `1px solid ${tintColor}${borderOpacity}` : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...sx,
      }}
    >
      {icon}
    </Box>
  );
};

export default TintIcon;
